import React, { useEffect, useRef, useState } from "react";
import { Clock, Search, Star, X } from "react-feather";
import { Link, useNavigate } from "react-router-dom";
import sidebarData from "./sidebar/sidebarData";
import Fuse from "fuse.js";

const SearchComponent = ({ onClose, setIsSearchVisible }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [searchInput, setSearchInput] = useState("");
  const [filteredResults, setFilteredResults] = useState([]);
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const searchInputRef = useRef(null);
  const [recentlyAccessed, setRecentlyAccessed] = useState([]);

  useEffect(() => {
    // Focus on the input field when the component mounts
    searchInputRef.current.focus();
  }, []);

  useEffect(() => {
    // Load recently accessed from local storage
    const storedRecentlyAccessed =
      JSON.parse(localStorage.getItem("recentlyAccessed")) || [];
    setRecentlyAccessed(storedRecentlyAccessed);
  }, []);

  const updateRecentlyAccessed = (page) => {
    // Remove the page if it already exists in the list to avoid duplicates
    const filtered = recentlyAccessed.filter(
      (item) => item.label !== page.label
    );
    // Add the new page to the beginning of the list
    const updatedRecentlyAccessed = [page, ...filtered];
    // Trim the list to keep only the last 5 items
    if (updatedRecentlyAccessed.length > 5) {
      updatedRecentlyAccessed.length = 5; // Keep only the last 5 entries
    }
    // Update the state and local storage
    setRecentlyAccessed(updatedRecentlyAccessed);
    localStorage.setItem(
      "recentlyAccessed",
      JSON.stringify(updatedRecentlyAccessed)
    );
  };

  useEffect(() => {
    // Load favorites from local storage
    const storedFavorites = JSON.parse(localStorage.getItem("favorites")) || [];
    setFavorites(storedFavorites);
  }, []);

  useEffect(() => {
    // Load favorites from local storage
    const storedFavorites = JSON.parse(localStorage.getItem("favorites")) || [];
    setFavorites(storedFavorites);
  }, []);

  useEffect(() => {
    localStorage.setItem("favorites", JSON.stringify(favorites));
  }, [favorites]);

  const toggleFavorite = (item) => {
    const isFavorite = favorites.find((fav) => fav.label === item.label);
    if (isFavorite) {
      // Remove from favorites
      setFavorites(favorites.filter((fav) => fav.label !== item.label));
    } else {
      // Add to favorites
      setFavorites([...favorites, item]);
    }
  };

  const handleOverlayClick = () => {
    setIsSearchVisible(true);
    onClose();
  };

  const handleInputClick = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    // Update window width when the component mounts
    const handleResize = () => setWindowWidth(window.innerWidth);

    // Add event listener for window resize
    window.addEventListener("resize", handleResize);

    // Remove event listener when the component unmounts
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        handleOverlayClick();
      }
    };

    // Add event listener for keydown
    window.addEventListener("keydown", handleEsc);

    // Remove event listener on cleanup
    return () => window.removeEventListener("keydown", handleEsc);
  }, [handleOverlayClick]);

  // Modify the overlayStyle and searchContainerStyle
  const overlayStyle = {
    position: "fixed",
    top: "0",
    left: "0",
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)", // Darker overlay color
    zIndex: "1000",
  };

  const searchContainerStyle = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "white",
    borderRadius: "10px",
    zIndex: "1000",
    backdropFilter: "blur(20px)",
    display: "flex",
    flexDirection: "column",
    height: "85vh",
  };

  const escBtn = {
    position: "absolute",
    top: "36%",
    right: "10%",
    zIndex: "900",
  };
  const searchBarStyle = {
    display: "flex",
    alignItems: "center",
    position: "relative",
    borderBottom: "1px solid black",
  };

  const searchIcon = {
    position: "absolute",
    top: "30%",
    left: "3%",
  };
  const closeIcon = {
    position: "absolute",
    top: "30%",
    right: "2%",
  };

  const inputStyle = {
    border: "none",
    outline: "none",
    fontSize: "large",
    backgroundColor: "transparent",
    width: "100%",
    color: "black",
    height: "60px",
    padding: "0 0 0 50px",
    position: "relative",
  };

  const shortcutsContainerStyle = {
    display: "flex",
    flexDirection: windowWidth <= 630 ? "row" : "column",
    justifyContent: "center",
    padding: "20px",
    flexWrap: "wrap",
    height: windowWidth <= 630 ? "400px" : "",
    overflowY: windowWidth <= 630 ? "scroll" : "",
    overflowX: "hidden",
    marginTop: "2px",
  };

  const shortcutRowStyle = {
    width: "80vw",
    maxWidth: "600px",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    // justifyContent: "space-between",
    flexDirection: "column",
  };

  const searchContentStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    maxHeight: "40vh",
    overflowX: "hidden",
    overflowY: "auto",
    fontSize: "16px",
    padding: "0 20px",
    width: "100%",
  };

  const shortcutContentStyle = {
    display: "flex",
    flexDirection: "column",
    padding: "0 20px",
    height: "30vh",
    overflowX: "hidden",
    overflowY: "auto",
  };

  const shortcutStyle = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
    fontSize: "16px",
    padding: "0 20px",
    width: "100%",
  };

  const searchItem = {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "left",
    justifyContent: "space-between",
    padding: "8px",
  };

  const favoritesAndRecent = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
    width: "100%",
    marginTop: "20px",
  };

  const handleEnterPress = (e) => {
    if (e.key === "Enter" && searchInput.trim()) {
      const options = {
        includeScore: true,
        keys: ["label"],
      };

      const fuse = new Fuse(flattenSidebarData(sidebarData), options);
      const result = fuse.search(searchInput.trim());

      if (result.length > 0) {
        // Redirect to the first match's linkName
        navigate(result[0].item.linkName);
        updateRecentlyAccessed(result[0].item);
        handleOverlayClick();
      }
    }
  };

  // Utility function to flatten the sidebar data structure
  const flattenSidebarData = (data) => {
    let flatData = [];
    Object.values(data).forEach((category) => {
      category.forEach((item) => {
        flatData.push(item);
        if (item.items) {
          item.items.forEach((subItem) =>
            flatData.push({ ...subItem, parent: item.label })
          );
        }
      });
    });
    return flatData;
  };

  const handleInputChange = (e) => {
    const input = e.target.value;
    setSearchInput(input);

    if (!input || input.length === 0) {
      setFilteredResults([]);
      return;
    }

    const results = [];
    Object.keys(sidebarData).forEach((category) => {
      sidebarData[category].forEach((item) => {
        if (item.label.toLowerCase().includes(input.toLowerCase())) {
          results.push(item);
        }
        if (item.items) {
          item.items.forEach((subItem) => {
            if (subItem.label.toLowerCase().includes(input.toLowerCase())) {
              results.push({ ...subItem, parentIcon: item.icon });
            }
          });
        }
      });
    });

    setFilteredResults(results);
  };

  return (
    <div>
      <div style={overlayStyle} onClick={handleOverlayClick}>
        <div style={searchContainerStyle} onClick={handleInputClick}>
          <div style={searchBarStyle}>
            <Search size={25} style={searchIcon} />
            <input
              type="text"
              style={inputStyle}
              placeholder="Type here ..."
              ref={searchInputRef}
              value={searchInput}
              onChange={handleInputChange}
              onKeyDown={handleEnterPress}
            />
            <label style={escBtn}>[ E S C ]</label>
            <X size={25} style={closeIcon} onClick={handleOverlayClick} />
          </div>
          <div style={shortcutsContainerStyle}>
            <div style={shortcutRowStyle}>
              <div style={shortcutStyle} className="popularSearchContainer">
                <div style={searchContentStyle}>
                  {filteredResults?.map((result, index) => (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Link
                        to={result.linkName}
                        key={index}
                        onClick={() => {
                          handleOverlayClick();
                          updateRecentlyAccessed(result);
                        }}
                        style={searchItem}
                      >
                        {result.icon ? (
                          <result.icon />
                        ) : result.parentIcon ? (
                          <result.parentIcon />
                        ) : null}
                        {result.label}
                      </Link>
                      <Star
                        size={20}
                        onClick={() => toggleFavorite(result)}
                        style={{
                          cursor: "pointer",
                          color: favorites.find(
                            (fav) => fav.label === result.label
                          )
                            ? "gold"
                            : "grey",
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div style={favoritesAndRecent}>
                <div style={shortcutStyle} className="popularSearchContainer">
                  <h4
                    style={{
                      fontSize: "large",
                      color: "grey",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    Favorites
                  </h4>
                  <div style={shortcutContentStyle}>
                    {favorites.map((fav, index) => (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                        key={index}
                      >
                        <Link
                          to={fav.linkName}
                          onClick={() => {
                            handleOverlayClick();
                            updateRecentlyAccessed(fav);
                          }}
                          style={searchItem}
                        >
                          {/* {fav.icon ? <fav.icon /> : null} */}
                          {fav.label}
                        </Link>
                        <Star
                          size={20}
                          onClick={() => toggleFavorite(fav)}
                          style={{ cursor: "pointer", color: "gold" }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                {/* Recently accessed section */}
                <div style={shortcutStyle} className="popularSearchContainer">
                  <h4
                    style={{
                      fontSize: "large",
                      color: "grey",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    Recently Accessed
                  </h4>
                  <div style={shortcutContentStyle}>
                    {recentlyAccessed.map((recent, index) => (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                        key={index}
                      >
                        <Link
                          to={recent.linkName}
                          onClick={() => {
                            handleOverlayClick();
                            updateRecentlyAccessed(recent);
                          }}
                          style={searchItem}
                        >
                          {recent.label}
                          {<Clock size={16} />}
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchComponent;
